import { useEffect, useState } from 'react';
import { useAuth, isJoinInProgress } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { knoxApi } from '@/lib/knox-api';
import { OAuthButtons } from './OAuthButtons';
import { EmailLoginForm } from './EmailLoginForm';

export function LoginPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('knox-aiops');
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [emailStep, setEmailStep] = useState<string>('email');

  // Read URL params once on mount
  const [urlTokens] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      inviteToken: params.get('invite_token'),
      joinToken: params.get('join_token'),
      error: params.get('error'),
    };
  });

  // Auto-redirect when already logged in with a workspace
  useEffect(() => {
    if (!loading && user?.default_workspace_id) {
      const params = new URLSearchParams(window.location.search);
      const redirect = params.get('redirect');
      const target = redirect && redirect.startsWith('/') && !redirect.startsWith('//')
        ? redirect
        : `/workspaces/${user.default_workspace_id}`;
      navigate(target, { replace: true });
    }
  }, [user, loading, navigate]);

  // Not logged in but has invite/join token → save for later auto-fill on org-binding.
  // Clear the opposite token so a stale token from a previous visit can't hijack the current intent.
  useEffect(() => {
    if (urlTokens.inviteToken) {
      localStorage.removeItem('pending_join_token');
      localStorage.setItem('pending_invite_token', urlTokens.inviteToken);
    }
    if (urlTokens.joinToken) {
      localStorage.removeItem('pending_invite_token');
      localStorage.setItem('pending_join_token', urlTokens.joinToken);
    }
  }, [urlTokens]);

  // Logged in but no org → auto-join or redirect to org-binding
  // Guard: only run when user has neither org_id nor default_workspace_id.
  // Users WITH an org are handled by the login/register callbacks in auth.tsx.
  useEffect(() => {
    if (loading || !user || user.org_id || user.default_workspace_id) return;

    const joinToken = localStorage.getItem('pending_join_token');
    if (joinToken) {
      localStorage.removeItem('pending_join_token');
      localStorage.removeItem('pending_invite_token');
      knoxApi.joinOrgLoggedIn({ token: joinToken })
        .then(() => { window.location.href = '/workspaces'; })
        .catch(() => navigate('/org-binding', { replace: true }));
      return;
    }

    const inviteToken = localStorage.getItem('pending_invite_token');
    localStorage.removeItem('pending_invite_token');
    if (inviteToken) {
      navigate(`/invite`, { state: { token: inviteToken }, replace: true });
      return;
    }

    // Don't navigate while consumeJoinToken is in-flight (register callback is handling it)
    if (isJoinInProgress()) return;

    navigate('/org-binding', { replace: true });
  }, [user, loading, navigate]);

  // Read OAuth error from URL
  useEffect(() => {
    if (urlTokens.error) {
      setOauthError(urlTokens.error);
      window.history.replaceState({}, '', '/login');
    }
  }, [urlTokens.error]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (user) return null;

  return (
    <div className="flex h-screen flex-col bg-white font-[Inter,sans-serif] text-foreground">
      {/* Header */}
      <header className="flex shrink-0 w-full items-center justify-between px-6 py-3 bg-white/80 backdrop-blur-md border-b border-black/[0.04]">
        <div className="flex items-center gap-2">
          <img src="/knox-logo.png" alt="Knox" className="h-7 w-auto" />
          <span className="font-heading text-xl font-extrabold tracking-tighter text-foreground">Knox</span>
        </div>
      </header>

      {/* Main */}
      <main className="relative flex flex-1 min-h-0 flex-col items-center justify-center overflow-auto px-4 py-6">
        <div className="knox-dot-grid pointer-events-none absolute inset-0 opacity-10" />
        <div className="knox-card-glass relative w-full max-w-[420px] p-6 sm:p-8">
          {/* Logo + Heading — logo always visible, title changes by step */}
          <div className={`${emailStep === 'verify-email' ? 'mb-4' : 'mb-6 sm:mb-8'} text-center`}>
            <div className="mb-3 flex justify-center">
              <img src="/knox-logo.png" alt="" className="h-10 sm:h-12 w-auto" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground mb-1">
              {emailStep === 'verify-email' ? t('loginForm.verifyEmailTitle')
                : emailStep === 'set-password' ? t('loginForm.setPasswordTitle')
                : emailStep === 'register' ? t('loginForm.registerTitle')
                : t('login.signIn')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {emailStep === 'set-password' ? t('loginForm.setPasswordSubtitle')
                : emailStep === 'verify-email' ? t('loginForm.verifyEmailSubtitle')
                : emailStep === 'register' ? t('loginForm.registerSubtitle')
                : t('login.subtitle')}
            </p>
          </div>

          {/* OAuth error banner */}
          {oauthError && (
            <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3">
              <p className="text-sm text-destructive">
                {t(`login.oauthError.${oauthError}`, t('login.oauthError.unknown'))}
              </p>
            </div>
          )}

          {/* OAuth buttons — only on email step (user hasn't chosen a login method yet) */}
          {emailStep === 'email' && (
            <>
              <div className="mb-6 sm:mb-8">
                <OAuthButtons inviteToken={urlTokens.inviteToken} joinToken={urlTokens.joinToken} />
              </div>

              {/* Divider */}
              <div className="mb-6 sm:mb-8 flex items-center">
                <div className="flex-1 border-t border-black/[0.16]" />
                <span className="mx-4 text-xs uppercase tracking-widest font-medium text-muted-foreground/40">or</span>
                <div className="flex-1 border-t border-black/[0.16]" />
              </div>
            </>
          )}

          {/* Email login form */}
          <EmailLoginForm onStepChange={setEmailStep} />
        </div>
      </main>

      {/* Footer */}
      <footer className="shrink-0 w-full bg-white">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-center gap-2 px-8 py-4">
          <span className="text-[0.6875rem] uppercase tracking-widest text-muted-foreground/60">{t('login.copyright')}</span>
          <div className="flex gap-6">
            <a href="/terms" className="text-[0.6875rem] uppercase tracking-widest text-muted-foreground/40 transition-colors hover:text-primary hover:underline underline-offset-4">{t('login.termsOfService')}</a>
            <a href="/privacy" className="text-[0.6875rem] uppercase tracking-widest text-muted-foreground/40 transition-colors hover:text-primary hover:underline underline-offset-4">{t('login.privacyPolicy')}</a>
          </div>
          <p className="text-[0.625rem] text-muted-foreground/40 text-center">
            Protected by reCAPTCHA —{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-muted-foreground/60">Privacy</a>
            {' & '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-muted-foreground/60">Terms</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
